import { Suspense, lazy, useEffect, useState } from "react";
import { api } from "./api.js";
import { t, useLang, ShieldCheckIcon, LangSwitch } from "./shared.jsx";

// Code-splitting: tot ce ține de aplicația autentificată (listă de cazuri,
// detaliu de caz, cele 12 taburi, export PDF cu jsPDF + fonturi Liberation
// Sans încorporate) e mult mai greu decât ecranul de login și nu e nevoie
// de el pentru primul randaj (LCP). React.lazy() îl mută într-un chunk
// separat, încărcat abia după autentificare — vezi audit PageSpeed
// (LCP 4.2s + 365 KiB JS neutilizat la încărcare).
const Shell = lazy(() => import("./Shell.jsx"));

export default function App() {
  const [user, setUser] = useState(() => (api.isAuthenticated() ? api.getCachedUser() : null));
  const [lang, setLang] = useLang();
  const [authView, setAuthView] = useState("login");

  useEffect(() => {
    api.setSessionExpiredHandler(() => setUser(null));
  }, []);

  if (!user) {
    return authView === "register" ? (
      <RegisterScreen onRegistered={setUser} onSwitchToLogin={() => setAuthView("login")} lang={lang} onLangChange={setLang} />
    ) : (
      <LoginScreen onLoggedIn={setUser} onSwitchToRegister={() => setAuthView("register")} lang={lang} onLangChange={setLang} />
    );
  }
  return (
    <Suspense fallback={<div className="loading">{t(lang, "Se încarcă...")}</div>}>
      <Shell user={user} onLogout={() => { api.logout(); setUser(null); }} onAccountUpdated={setUser} lang={lang} onLangChange={setLang} />
    </Suspense>
  );
}

function LoginScreen({ onLoggedIn, onSwitchToRegister, lang, onLangChange }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await api.login(email, password);
      onLoggedIn(user);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-screen">
      <div className="login-hero">
        <div className="login-hero-top">
          <div className="login-icon"><ShieldCheckIcon size={22} className="text-white" /></div>
          <LangSwitch lang={lang} onChange={onLangChange} />
        </div>
        <div className="login-brand">{t(lang, "INVESTIGAȚII · SUITA HUB CLIENT")}</div>
        <h1>{t(lang, "Managementul cazurilor")}<br />{t(lang, "de investigație")}</h1>
        <p className="login-tagline">
          {t(lang, "De la primul raport de incident până la analiza Tripod Beta și interviul investigativ — un singur loc pentru toată documentația unui caz.")}
        </p>
        <div className="tiv-badge">
          {t(lang, "Made by")} <span className="tiv-mark">WKC</span>
        </div>
      </div>
      <div className="login-panel">
        <div className="login-panel-inner">
          <div className="section-label">{t(lang, "Autentificare")}</div>
          <form onSubmit={submit}>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" required autoFocus />
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t(lang, "Parolă")} required />
            {error && <div className="error">{error}</div>}
            <button type="submit" disabled={loading}>{loading ? t(lang, "Se autentifică…") : t(lang, "Intră în platformă")}</button>
          </form>
          <p className="auth-switch">
            {t(lang, "Nu ai cont?")} <button type="button" className="link-btn-inline" onClick={onSwitchToRegister}>{t(lang, "Creează unul")}</button>
          </p>
        </div>
      </div>
    </div>
  );
}

// Înregistrare self-service: primul cont (Owner) al unei organizații noi
// ia naștere direct de aici, fără intervenție manuală — organizația și
// registrele implicite (4M + bază de termeni Tripod) se creează automat pe
// backend. Nu există verificare de email în acest ecosistem (fără
// infrastructură de email), deci accesul e imediat după înregistrare,
// exact ca la login — de aceea ruta e limitată mult mai strict decât
// login-ul (vezi REGISTER_THROTTLE pe backend).
function RegisterScreen({ onRegistered, onSwitchToLogin, lang, onLangChange }) {
  const [organizationName, setOrganizationName] = useState("");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setError("");
    if (password !== confirmPassword) {
      setError(t(lang, "Parola nouă și confirmarea nu coincid."));
      return;
    }
    setLoading(true);
    try {
      const user = await api.register(organizationName, fullName, email, password);
      onRegistered(user);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="login-screen">
      <div className="login-hero">
        <div className="login-hero-top">
          <div className="login-icon"><ShieldCheckIcon size={22} className="text-white" /></div>
          <LangSwitch lang={lang} onChange={onLangChange} />
        </div>
        <div className="login-brand">{t(lang, "INVESTIGAȚII · SUITA HUB CLIENT")}</div>
        <h1>{t(lang, "Managementul cazurilor")}<br />{t(lang, "de investigație")}</h1>
        <p className="login-tagline">
          {t(lang, "De la primul raport de incident până la analiza Tripod Beta și interviul investigativ — un singur loc pentru toată documentația unui caz.")}
        </p>
        <div className="tiv-badge">
          {t(lang, "Made by")} <span className="tiv-mark">WKC</span>
        </div>
      </div>
      <div className="login-panel">
        <div className="login-panel-inner">
          <div className="section-label">{t(lang, "Cont nou pentru organizația ta")}</div>
          <form onSubmit={submit}>
            <input value={organizationName} onChange={(e) => setOrganizationName(e.target.value)} placeholder={t(lang, "Numele organizației")} required autoFocus />
            <input value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder={t(lang, "Numele tău complet")} required />
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" required />
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={t(lang, "Parolă (minim 10 caractere)")} minLength={10} required />
            <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} placeholder={t(lang, "Confirmă parola")} required />
            {error && <div className="error">{error}</div>}
            <button type="submit" disabled={loading}>{loading ? t(lang, "Se creează contul…") : t(lang, "Creează organizația")}</button>
          </form>
          <p className="auth-switch">
            {t(lang, "Ai deja cont?")} <button type="button" className="link-btn-inline" onClick={onSwitchToLogin}>{t(lang, "Intră în platformă")}</button>
          </p>
        </div>
      </div>
    </div>
  );
}
